/**
 * Hook: GET /admin/super-admins — list every super-admin user (RBAC FE WS-D).
 *
 * The super-admin users console surface. SA-host only: the route + nav link are
 * gated on `useAuthStore.isSuperAdmin` (the JWT `is_sa` claim) and hidden while
 * impersonating (invariants #4 / #8). This hook is the read side; gating is
 * cosmetic — the backend `current_super_admin` dependency is the real boundary
 * (`app/api/admin/sa_users.py:133-152`).
 *
 * Response shape per `list_super_admins` (`sa_users.py:140-152`):
 * `{ items: SaUser[] }`. A flagged-SA `password_change_required` 403 is
 * intercepted globally by the WS-E apiClient handler and redirected to
 * `/admin/change-password`, so this hook never has to special-case it.
 *
 * #8 (no SA-identity leak under impersonation): the read is gated by `enabled`.
 * An impersonating session is STILL an SA principal at the backend, so this
 * endpoint would 200 with the full SA roster even while impersonating — the
 * render-layer early-return alone doesn't stop the GET, which would land SA
 * emails in the impersonated session's query cache. Callers MUST pass
 * `enabled: impersonation === null` so SA identity is never fetched while
 * impersonating.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { saUsersKey } from "./saUserKeys";

export interface SaUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  requires_password_reset: boolean;
  created_at_utc: string;
}

interface SaUsersResponse {
  items: SaUser[];
}

export function useSuperAdmins(
  enabled: boolean,
): UseQueryResult<SaUser[], ApiError> {
  return useQuery<SaUser[], ApiError>({
    queryKey: saUsersKey(),
    queryFn: async () => {
      const resp = await apiClient.get<SaUsersResponse>("/admin/super-admins");
      return resp.data.items;
    },
    enabled,
  });
}
